import { Link } from "react-router-dom"

import type { BatteryProposalDto } from "~/api/dto/battery-proposal.dto"
import useBatteryProposalFinancialSummary from "~/api/hooks/useBatteryProposalFinancialSummary"
import EnvVars from "~/env-vars"

import { isBatteryProposalReadOnly } from "./battery-proposal-read-only"
import { BatteryPresentation } from "./BatteryPresentation"
import { BillImpact } from "./sections/bill-impact"
import { SetupFab } from "./sections/chrome"
import { batterySetupStepPath } from "./setup/setup-steps"
import { useBatteryUpdates } from "./use-battery-updates"
import { renderableArbitrage } from "./view-helpers"

/**
 * The seller's presentation. `arbitrage.result` is the server's answer to what
 * this Proposal may currently claim — null for a pending, unavailable, failed
 * or outdated run — so the page shows savings only while a current saved
 * calculation describes it, and every section shows that one calculation.
 * The AI compute payout's split at the monthly payment comes from the same
 * financial summary read the investment card renders.
 */
export const BatteryOnlyProposalView = ({
  proposal,
}: {
  proposal: BatteryProposalDto
}): JSX.Element => {
  const arbitrage = EnvVars.BATTERY_SAVINGS_ENABLED
    ? renderableArbitrage(proposal.arbitrage.result)
    : null
  const updates = useBatteryUpdates(proposal)
  const financialSummary = useBatteryProposalFinancialSummary(proposal.id, {
    enabled: proposal.proposalCost != null && proposal.aiCompute != null,
  })

  return (
    <BatteryPresentation
      proposal={proposal}
      arbitrage={arbitrage}
      rail={updates.rail}
      chrome={
        <>
          <SetupFab proposal={proposal} />
          {updates.panel}
        </>
      }
      investment={<BillImpact proposal={proposal} arbitrage={arbitrage} />}
      computePaymentShare={financialSummary.data?.aiComputePaymentShare ?? null}
      computeAction={
        isBatteryProposalReadOnly(proposal) ? null : (
          <Link
            to={batterySetupStepPath(proposal.id, `equipment`)}
            data-testid="battery-ai-compute-change"
            className="rounded-full border border-bp-border-strong px-3 py-1 text-[length:var(--step--2)] font-semibold text-bp-text hover:border-bp-accent hover:text-bp-accent"
          >
            Change in Setup
          </Link>
        )
      }
    />
  )
}
